/*!
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import { tokens } from '@neo4j-ndl/base';
import classNames from 'classnames';
import type React from 'react';

import { type CommonProps } from '../_common/types';

export type TagSize = 'x-small' | 'small' | 'medium' | 'large';

export const CategoricalColors = [
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
] as const;

export type TagColor = (typeof CategoricalColors)[number];

export interface TagBaseProps {
  /** Categorical color for the background */
  color?: TagColor;
  /** The content of the tag */
  children: React.ReactNode;
  /** Id applied to the content element used for aria-describedby on actions */
  contentId?: string;
  /** Icon to display before the content */
  leadingVisual?: React.ReactNode;
  /** Size of the tag */
  size?: TagSize;
  /** Trailing content rendered after the content (e.g. a remove button) */
  trailingContent?: React.ReactNode;
}

export const colorMap: Record<
  TagColor,
  {
    '--tag-background-color': string;
    '--tag-text-color': string;
  }
> = {
  '1': {
    '--tag-background-color': tokens.categorical[1],
    '--tag-text-color': tokens.palette.neutral[75],
  },
  '2': {
    '--tag-background-color': tokens.categorical[2],
    '--tag-text-color': tokens.palette.neutral[10],
  },
  '3': {
    '--tag-background-color': tokens.categorical[3],
    '--tag-text-color': tokens.palette.neutral[75],
  },
  '4': {
    '--tag-background-color': tokens.categorical[4],
    '--tag-text-color': tokens.palette.neutral[10],
  },
  '5': {
    '--tag-background-color': tokens.categorical[5],
    '--tag-text-color': tokens.palette.neutral[75],
  },
  '6': {
    '--tag-background-color': tokens.categorical[6],
    '--tag-text-color': tokens.palette.neutral[75],
  },
  '7': {
    '--tag-background-color': tokens.categorical[7],
    '--tag-text-color': tokens.palette.neutral[10],
  },
  '8': {
    '--tag-background-color': tokens.categorical[8],
    '--tag-text-color': tokens.palette.neutral[10],
  },
};

export const TagBase = ({
  size = 'medium',
  color,
  children,
  className,
  contentId,
  htmlAttributes,
  leadingVisual,
  trailingContent,
  ref,
  style,
  ...restProps
}: CommonProps<'div', TagBaseProps>) => {
  const classes = classNames('ndl-tag-new', className, {
    'ndl-large': size === 'large',
    'ndl-medium': size === 'medium',
    'ndl-small': size === 'small',
    'ndl-tag-colored': Boolean(color),
    'ndl-x-small': size === 'x-small',
  });

  const styles = {
    ...style,
    ...(color ? colorMap[color] : {}),
  } as React.CSSProperties;

  return (
    <div
      className={classes}
      ref={ref}
      style={styles}
      {...htmlAttributes}
      {...restProps}
    >
      {Boolean(leadingVisual) && (
        <div className="ndl-tag-leading-element">{leadingVisual}</div>
      )}
      <span className="ndl-tag-content" id={contentId}>
        {children}
      </span>
      {trailingContent}
    </div>
  );
};
